import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showSnackbar } from "../../redux/slices/auth.slice";
import {
  appendNotifications,
  markAsRead,
  markAllAsRead,
  setError,
  setLoading,
  setLoadingMore,
  setNotifications,
} from "../../redux/slices/notification.slice";
import { RootState } from "../../redux/store/store";
import {
  useNotificationControllerServiceGetAllNotifications,
  useNotificationControllerServiceMarkAsRead,
} from "../../services/queries/queries";
import { NotificationControllerService } from "../../services/requests/services.gen";

const PAGE_SIZE = 15;

const mapNotification = (item: any) => ({
  uuid: item.uuid,
  notificationType: item.notificationType,
  data: item.data,
  userUuid: item.userUuid,
  isRead: item.isRead,
  created: item.created,
});

export const useNotifications = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentPage, totalPages } = useSelector(
    (state: RootState) => state.notification,
  );
  const isFetchingMore = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const {
    data: notificationsResponse,
    isLoading,
    error,
    refetch,
  } = useNotificationControllerServiceGetAllNotifications(
    {
      userUuid: user?.userId || "",
      page: 0,
      pageSize: PAGE_SIZE,
      isRead: false,
      sortBy: "created",
      sortDirection: "DESC",
    },
    undefined,
    {
      enabled: !!user?.uuid,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      refetchInterval: 30 * 1000,
      refetchIntervalInBackground: true,
    },
  );

  const { mutateAsync: markAsReadMutation } =
    useNotificationControllerServiceMarkAsRead({
      onSuccess: (response: any) => {
        dispatch(
          showSnackbar({
            message: response?.message,
            severity: "success",
          }),
        );
        refetch();
      },
      onError: (err: any) => {
        dispatch(
          showSnackbar({
            message: err?.body?.message,
            severity: "error",
          }),
        );
      },
    });

  useEffect(() => {
    dispatch(setLoading(isLoading));
  }, [isLoading, dispatch]);

  useEffect(() => {
    if (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch notifications";
      dispatch(setError(errorMessage));
    } else {
      dispatch(setError(null));
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (notificationsResponse?.data) {
      const content = (notificationsResponse.data.content as any[]) ?? [];
      dispatch(
        setNotifications({
          notifications: content.map(mapNotification),
          totalElements:
            (notificationsResponse.data.totalElements as number) ?? 0,
          totalPages: (notificationsResponse.data.totalPages as number) ?? 0,
        }),
      );
    } else if (notificationsResponse) {
      dispatch(
        setNotifications({
          notifications: [],
          totalElements: 0,
          totalPages: 0,
        }),
      );
    }
  }, [notificationsResponse, dispatch]);

  const loadMore = async () => {
    const nextPage = currentPage + 1;
    if (isFetchingMore.current || nextPage >= totalPages || !user?.userId)
      return;

    isFetchingMore.current = true;
    setIsLoadingMore(true);
    dispatch(setLoadingMore(true));
    try {
      const response = await NotificationControllerService.getAllNotifications({
        userUuid: user.userId,
        page: nextPage,
        pageSize: PAGE_SIZE,
        isRead: false,
        sortBy: "created",
        sortDirection: "DESC",
      });
      const content = ((response as any)?.data?.content as any[]) ?? [];
      dispatch(
        appendNotifications({
          notifications: content.map(mapNotification),
          page: nextPage,
        }),
      );
    } catch {
    } finally {
      isFetchingMore.current = false;
      setIsLoadingMore(false);
      dispatch(setLoadingMore(false));
    }
  };

  const handleMarkAsRead = async (notificationUuid: string) => {
    if (notificationUuid) {
      if (
        notificationUuid.startsWith("sample-") ||
        !user?.uuid ||
        !notificationsResponse?.data?.content
      ) {
        dispatch(markAsRead(notificationUuid));
      } else {
        try {
          await markAsReadMutation({
            notificationUuid,
            userUuid: user.uuid,
          });
          dispatch(markAsRead(notificationUuid));
        } catch (error) {}
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.uuid || !notificationsResponse?.data?.content) {
      dispatch(markAllAsRead());
      return;
    }

    const content = notificationsResponse.data.content as any[];
    const unreadNotifications = content.filter((item: any) => !item.isRead);

    if (unreadNotifications.length === 0) {
      return;
    }

    try {
      const markReadPromises = unreadNotifications.map((notification: any) =>
        markAsReadMutation({
          notificationUuid: notification.uuid,
          userUuid: user.uuid,
        }),
      );

      await Promise.all(markReadPromises);

      dispatch(markAllAsRead());
      refetch();
    } catch (error: any) {}
  };

  return {
    refetch,
    isLoading,
    isLoadingMore,
    hasMore: currentPage + 1 < totalPages,
    loadMore,
    error,
    markAsRead: handleMarkAsRead,
    isMarkingRead: markAsReadMutation,
    markAllAsRead: handleMarkAllAsRead,
  };
};
